import { useEffect, useRef, useState, type ReactNode } from "react";
import type { PageNode } from "../../../shared/src/pages";
import type { PublicImage } from "../../../shared/src/resized-images";

type LinkNode = Extract<PageNode, { kind: "link" }>;

/** The attributes a link button's layout reads: a second line, or a thumbnail or code beside it. */
export function pageLinkAttributes(node: LinkNode) {
  return {
    "data-page-link-subtitle": node.subtitle ? "" : undefined,
    "data-page-link-sides": node.thumbnail || node.code ? "" : undefined,
  };
}

/**
 * What a link button holds: its label and second line, centered between a thumbnail at its
 * start and a code at its end. Either side drops the outbound arrow. `arrow` draws that arrow,
 * so the canvas can use its own icon.
 */
export function PageLinkContent(props: {
  node: LinkNode;
  label: string;
  thumbnail?: PublicImage;
  copied?: boolean;
  arrow: ReactNode;
}) {
  const { node, label } = props;
  const sides = !!(node.thumbnail || node.code);
  return (
    <>
      {node.thumbnail && (
        <img
          className="drate-page-link-thumb"
          src={props.thumbnail?.src ?? node.thumbnail}
          srcSet={props.thumbnail?.srcset}
          sizes={props.thumbnail?.srcset ? "48px" : undefined}
          alt=""
          loading="lazy"
          decoding="async"
        />
      )}
      <span className="drate-page-link-copy">
        <span>{label}</span>
        {node.subtitle && <span className="drate-page-link-subtitle">{node.subtitle}</span>}
      </span>
      {node.code && (
        <span className="drate-page-link-code" data-copied={props.copied ? "" : undefined}>
          {node.code}
        </span>
      )}
      {!sides && props.arrow}
    </>
  );
}

/**
 * A button with a discount code: pressing it copies the code, then opens its destination in a
 * new tab, so the visitor keeps the code and the page. Without a destination it only copies.
 * Where the clipboard is unavailable or refused, the code stays readable and the link still opens.
 */
export function CodeLink(props: {
  node: LinkNode & { code: string };
  label: string;
  href?: string;
  thumbnail?: PublicImage;
  attributes: Record<string, unknown>;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const copy = () => {
    navigator.clipboard?.writeText(props.node.code).then(
      () => {
        setCopied(true);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), 2000);
      },
      () => undefined,
    );
  };
  const content = (
    <>
      <PageLinkContent
        node={props.node}
        label={props.label}
        thumbnail={props.thumbnail}
        copied={copied}
        arrow={null}
      />
      <span className="sr-only" aria-live="polite">
        {copied ? "Code copied" : ""}
      </span>
    </>
  );
  const attributes = {
    ...props.attributes,
    ...pageLinkAttributes(props.node),
    className: "drate-page-node drate-page-link",
    onClick: copy,
  };
  if (!props.href)
    return (
      <button type="button" {...attributes}>
        {content}
      </button>
    );
  const away = /^https:/i.test(props.href);
  return (
    <a
      {...attributes}
      href={props.href}
      target={away ? "_blank" : undefined}
      rel={away ? "noopener noreferrer" : undefined}
    >
      {content}
    </a>
  );
}
